import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Plus, Settings, Trash2 } from "lucide-react";
import { PageFilterPopover } from "../components/page-filter-popover";
import { usePageHeader } from "../lib/use-page-header";
import { api, type ServiceOut } from "../api";
import { fmtMoney, toMoneyRaw } from "../lib/format";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { MoneyInput } from "../components/money-input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";

export function ServicesPage() {
  const { t } = useTranslation(["settings", "common"]);
  const [items, setItems] = useState<ServiceOut[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<ServiceOut | "new" | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<ServiceOut | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setItems(await api.services(showInactive));
      } finally {
        setLoading(false);
      }
    })();
  }, [showInactive, refresh]);

  async function del(id: number) {
    try {
      await api.deleteService(id);
      toast.success(t("services.deleted"));
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error((e as { message?: string }).message || t("services.error"));
    } finally {
      setConfirm(null);
    }
  }

  const filterCount = showInactive ? 1 : 0;
  const filterNode = useMemo(
    () => (
      <PageFilterPopover
        count={filterCount}
        onClear={() => setShowInactive(false)}
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="size-4 rounded border-input accent-primary"
          />
          <span>{t("services.showInactive")}</span>
        </label>
      </PageFilterPopover>
    ),
    [showInactive, filterCount, t],
  );

  const actionsNode = useMemo(
    () => (
      <Button size="sm" onClick={() => setEditing("new")}>
        <Plus />
        <span className="hidden sm:inline">{t("services.newService")}</span>
      </Button>
    ),
    [t],
  );

  usePageHeader({ filters: filterNode, actions: actionsNode, filterCount });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Settings />}
              title={t("services.empty.title")}
              description={t("services.empty.desc")}
              action={
                <Button onClick={() => setEditing("new")}>
                  <Plus />
                  {t("services.newService")}
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("services.table.name")}</TableHead>
                  <TableHead className="text-right">{t("services.table.price")}</TableHead>
                  <TableHead className="text-right">{t("services.table.intervalKm")}</TableHead>
                  <TableHead className="text-right">{t("services.table.intervalMonths")}</TableHead>
                  <TableHead>{t("services.table.smsText")}</TableHead>
                  <TableHead>{t("services.table.status")}</TableHead>
                  <TableHead className="text-right">{t("services.table.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className={s.is_active ? "" : "opacity-60"}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(s.narx)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.interval_km ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.interval_months ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {s.sms_text || "—"}
                    </TableCell>
                    <TableCell>
                      {s.is_active ? (
                        <Badge variant="success">{t("services.active")}</Badge>
                      ) : (
                        <Badge variant="outline">{t("services.inactive")}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(s)}
                      >
                        <Pencil />
                        {t("services.edit")}
                      </Button>
                      {s.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirm(s)}
                        >
                          <Trash2 />
                          {t("services.delete")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <ServiceForm
            item={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setRefresh((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        {confirm && (
          <>
            <DialogHeader>
              <DialogTitle>{t("services.confirm.title")}</DialogTitle>
              <DialogDescription>
                {t("services.confirm.desc", { name: confirm.name })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirm(null)}>
                {t("common:actions.cancel")}
              </Button>
              <Button variant="destructive" onClick={() => del(confirm.id)}>
                {t("services.delete")}
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}

export function ServiceForm({
  item,
  onCancel,
  onSaved,
}: {
  item: ServiceOut | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["settings", "common"]);
  const [name, setName] = useState(item?.name ?? "");
  const [code, setCode] = useState(item?.code ?? "");
  const [narx, setNarx] = useState(toMoneyRaw(item?.narx));
  const [izoh, setIzoh] = useState(item?.izoh ?? "");
  const [km, setKm] = useState(item?.interval_km?.toString() ?? "");
  const [months, setMonths] = useState(item?.interval_months?.toString() ?? "");
  const [smsText, setSmsText] = useState(item?.sms_text ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Yangi xizmatda narx + eslatma matni majburiy (backend ham talab qiladi).
    // Tahrirlashda eski yozuvlar bloklanmasin — faqat create'da tekshiramiz.
    if (!item && (narx === "" || smsText.trim() === "")) {
      setErr(t("services.form.validation"));
      return;
    }
    setBusy(true);
    setErr(null);
    const data: Record<string, unknown> = {
      name,
      narx: narx === "" ? null : narx,
      izoh: izoh.trim() || null,
      interval_km: km ? parseInt(km, 10) : null,
      interval_months: months ? parseInt(months, 10) : null,
      sms_text: smsText || null,
    };
    // Yangi xizmatda bo'sh kod serverning mavjud avtomatik generatoriga
    // topshiriladi; tahrirda bo'shatish esa saqlangan kodni o'zgartirmaydi.
    if (code.trim()) data.code = code.trim();
    else if (!item) data.code = null;
    try {
      if (item) await api.updateService(item.id, data);
      else await api.createService(data);
      toast.success(item ? t("services.form.updated") : t("services.form.added"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("services.form.error"));
    } finally {
      setBusy(false);
    }
  }

  const preview = smsText
    ? smsText
        .replaceAll("{ustaxona}", "AvtoServis")
        .replaceAll("{mijoz}", "Ali")
        .replaceAll("{avto}", "01A123BB")
        .replaceAll("{client_name}", "Ali")
        .replaceAll("{plate}", "01A123BB")
        .replaceAll("{service}", name || t("services.form.previewService"))
        .replaceAll("{workshop}", "AvtoServis")
    : "";

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>
          {item ? t("services.form.editTitle") : t("services.form.newTitle")}
        </DialogTitle>
        <DialogDescription>
          {t("services.form.desc")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="svc-name">{t("services.form.nameLabel")}</Label>
          <Input
            id="svc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("services.form.namePlaceholder")}
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="svc-code">{t("services.form.codeLabel")}</Label>
          <Input
            id="svc-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="oil_change"
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            {t("services.form.codeHint")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="svc-narx">{t("services.form.narxLabel", { unit: t("common:units.som") })}</Label>
            <MoneyInput
              id="svc-narx"
              value={narx}
              onChange={setNarx}
              placeholder="150000"
              required={!item}
              disabled={busy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="svc-izoh">{t("services.form.izohLabel")}</Label>
            <Input
              id="svc-izoh"
              value={izoh}
              onChange={(e) => setIzoh(e.target.value)}
              maxLength={200}
              placeholder={t("services.form.izohPlaceholder")}
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">
              {t("services.form.izohHint")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="svc-km">{t("services.form.kmLabel")}</Label>
            <Input
              id="svc-km"
              value={km}
              onChange={(e) => setKm(e.target.value)}
              type="number"
              min="1"
              placeholder="10000"
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">
              {t("services.form.kmHint")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="svc-months">{t("services.form.monthsLabel")}</Label>
            <Input
              id="svc-months"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              type="number"
              min="1"
              placeholder="6"
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">
              {t("services.form.monthsHint")}
            </p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="svc-sms">{t("services.form.smsLabel")}</Label>
          <Textarea
            id="svc-sms"
            value={smsText}
            onChange={(e) => setSmsText(e.target.value)}
            rows={3}
            placeholder={t("services.form.smsPlaceholder")}
            required={!item}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            {t("services.form.smsHint")}{" "}
            <code className="bg-muted rounded px-1.5 py-0.5">{`{client_name}`}</code>{" "}
            <code className="bg-muted rounded px-1.5 py-0.5">{`{plate}`}</code>{" "}
            <code className="bg-muted rounded px-1.5 py-0.5">{`{service}`}</code>{" "}
            <code className="bg-muted rounded px-1.5 py-0.5">{`{workshop}`}</code>
          </p>
        </div>
        {preview && (
          <div className="rounded-md border-l-4 border-primary bg-primary/5 px-3 py-2 text-sm">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-1">
              {t("services.form.preview")}
            </div>
            {preview}
          </div>
        )}
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? t("common:actions.saving") : t("common:actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}
